import dayjs from "dayjs";
import { ColDef } from "ag-grid-community";
import { MenuNames } from "../../../data/menu-names";
import { useRecoilValue } from "recoil";
import { ChangeEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Member } from "../../../common/types/member";
import { request } from "../../../common/api";
import { Buttons } from "../../../components/atoms/Buttons";
import { GET_MEMBERS } from "../../../common/query-keys";
import { BaseResponse } from "../../../common/types/baseRespones";
import { permissionSelector } from "../../../common/states/permission";
import { Grid } from "../../../components/molecules/Grid";
import { InputText } from "../../../components/atoms/Inputs/InputText";
import { InputSelect } from "../../../components/atoms/Inputs/InputSelect";
import { PageContent } from "../../../components/organisms/Contents/PageContent";
import { confirmDialog } from "../../../components/containers/ConfirmDialog";
import PermissionComponent from "../../../components/atoms/PermissionComponent";
import { SearchContainer } from "../../../components/containers/SearchContainer";
import { MemberCreateModal } from "../../../components/containers/Member/MemberCreateModal";
import { MemberUpdateModal } from "../../../components/containers/Member/MemberUpdateModal";
import { ReactComponent as IconDelete } from "../../../styles/assets/svg/icon_edit_delete.svg";
import { ReactComponent as IconModify } from "../../../styles/assets/svg/icon_edit_modify.svg";

const searchKeywordSelectOptions = [
  { value: "mbrUserId", label: "사용자ID" },
  { value: "mbrUserDepartment", label: "소속" },
  { value: "mbrUserRank", label: "계급" },
  { value: "mbrUserName", label: "성명" },
  { value: "mbrUserTell", label: "연락처" },
  { value: "mbrFirstTime", label: "등록일시" },
  { value: "mbrUserGroup", label: "권한" },
];

export const AccountPage = () => {
  const queryClient = useQueryClient();
  const permission = useRecoilValue(
    permissionSelector(MenuNames.ACCOUNT_MANAGEMENT)
  );

  const [modalShow, setModalShow] = useState(false);
  const [updateModalShow, setupdateModalShow] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member>();
  const [searchInputType, setSearchInputType] = useState("text");
  const [search, setSearch] = useState({});

  const { data: members } = useQuery({
    queryKey: [GET_MEMBERS, search],
    queryFn: async () => {
      return await request<Member[]>({
        method: "GET",
        url: "/member/search",
        params: search,
      });
    },
  });

  const { mutateAsync: deleteMember } = useMutation({
    mutationFn: (mbrId: number) => {
      return request<BaseResponse>({
        method: "DELETE",
        url: `/member/${mbrId}`,
      });
    },
    onSuccess: (res: BaseResponse) => {
      queryClient.invalidateQueries({ queryKey: [GET_MEMBERS] });
    },
  });

  const handleSearch = (search: any) => {
    setSearch(search);
  };

  const handleSearchKeywordChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === "mbrFirstTime") {
      setSearchInputType("date");
    } else {
      setSearchInputType("text");
    }
  };

  const editButtonRender = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconModify />}
        onClick={() => {
          setSelectedMember(e.data);
          setupdateModalShow(true);
        }}
      />
    );
  };

  const deleteButtonRender = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconDelete />}
        onClick={async () => {
          const isConfirm = await confirmDialog({
            text: "삭제할 항목을 삭제하시겠습니까?",
          });

          if (isConfirm) {
            deleteMember(e.data.mbrId);
          }
        }}
      />
    );
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "No",
      valueGetter: "node.rowIndex + 1",
      width: 60,
    },
    {
      headerName: "사용자ID",
      field: "mbrUserId",
      flex: 1,
    },
    {
      headerName: "소속",
      field: "mbrUserDepartment",
      flex: 1,
    },
    {
      headerName: "계급",
      field: "mbrUserRank",
      flex: 1,
    },
    {
      headerName: "성명",
      field: "mbrUserName",
      flex: 1,
    },
    {
      headerName: "연락처",
      field: "mbrUserTell",
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: "등록일시",
      field: "mbrFirstTime",
      valueGetter: (params: any) =>
        dayjs(params.data.mbrFirstTime).format("YYYY.MM.DD HH:mm:ss"),
      flex: 1,
    },
    {
      headerName: "권한",
      field: "mbrUserGroup",
      flex: 1,
    },
    {
      headerName: "수정",
      cellRenderer: editButtonRender,
      width: 60,
      hide: permission?.pmsMenuUpdate !== 1,
    },
    {
      headerName: "삭제",
      cellRenderer: deleteButtonRender,
      width: 60,
      hide: permission?.pmsMenuDelete !== 1,
    },
  ];

  return (
    <>
      <PageContent
        depth01Title="관리자 설정"
        depth02Title="사용자 관리"
        depth03Title="계정 관리"
      >
        <>
          <SearchContainer
            onSearch={handleSearch}
            components={[
              <InputSelect
                name="searchKeyword"
                size="md"
                placeholder="구분"
                options={searchKeywordSelectOptions}
                onChange={handleSearchKeywordChange}
              />,
              <InputText
                type={searchInputType}
                name="searchValue"
                size="md"
                placeholder="Search"
              />,
            ]}
            gridButtons={
              <PermissionComponent hasPermission={permission?.pmsMenuInsert}>
                <Buttons
                  type="button"
                  size="md"
                  layout="primary"
                  label="신규 등록"
                  onClick={() => {
                    setModalShow(!modalShow);
                  }}
                />
              </PermissionComponent>
            }
          />
          <Grid rowData={members} columnDefs={columnDefs} />
        </>
      </PageContent>
      {modalShow && (
        <MemberCreateModal
          modalType="create"
          setModalShow={setModalShow}
          formId="create_user"
        />
      )}
      {updateModalShow && selectedMember && (
        <MemberUpdateModal
          member={selectedMember}
          modalType="modify"
          setModalShow={setupdateModalShow}
          formId="update_user"
        />
      )}
    </>
  );
};

// import { ChangeEvent, useState } from "react";
// import { useQuery } from "@tanstack/react-query";
// import { request } from "../../../common/api";
// import { GET_MEMBERS } from "../../../common/query-keys";
// import { Member } from "../../../common/types/member";
// import { Buttons } from "../../../components/atoms/Buttons";
// import { InputSelect } from "../../../components/atoms/Inputs/InputSelect";
// import { InputText } from "../../../components/atoms/Inputs/InputText";
// import { SearchContainer } from "../../../components/containers/SearchContainer";
// import { Grid } from "../../../components/molecules/Grid";
// import { ModalUser } from "../../../components/molecules/Modals/ModalUser";
// import { PageContent } from "../../../components/organisms/Contents/PageContent";
// import { ReactComponent as IconModify } from "../../../styles/assets/svg/icon_edit_modify.svg";
// import { ReactComponent as IconDelete } from "../../../styles/assets/svg/icon_edit_delete.svg";
// import { ColDef } from "ag-grid-community";

// export const AccountPage = () => {
//   const [modalShow, setModalShow] = useState(false);
//   const [search, setSearch] = useState({});
//   const [searchInputType, setSearchInputType] = useState("text");

//   const columnDefs: ColDef[] = [
//     {
//       type: "number",
//       headerName: "No",
//       field: "mbrId",
//       width: 60,
//     },
//     {
//       headerName: "사용자ID",
//       field: "mbrUserId",
//       width: 140,
//     },
//     {
//       headerName: "소속",
//       field: "mbrUserDepartment",
//       width: 120,
//     },
//     {
//       headerName: "계급",
//       field: "mbrUserRank",
//       width: 80,
//     },
//     {
//       headerName: "성명",
//       field: "mbrUserName",
//       width: 100,
//     },
//     {
//       headerName: "연락처",
//       field: "mbrUserTell",
//       minWidth: 140,
//       flex: 1,
//     },
//     {
//       headerName: "등록일시",
//       field: "mbrFirstTime",
//       width: 160,
//     },
//     {
//       headerName: "권한",
//       field: "mbrUserGroup",
//       width: 100,
//     },
//     {
//       headerName: "수정",
//       width: 60,
//     },
//     {
//       headerName: "삭제",
//       width: 60,
//     },
//   ];

//   const { data: members } = useQuery({
//     queryKey: [GET_MEMBERS, search],
//     queryFn: async () => {
//       return await request<Member[]>({
//         method: "GET",
//         url: "/member/search",
//         params: search,
//       });
//     },
//   });

//   const handleSearch = (search: any) => {
//     setSearch(search);
//   };

//   const handleSearchTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
//     if (e.target.value === "mbrFirstTime") {
//       setSearchInputType("date");
//     } else {
//       setSearchInputType("text");
//     }
//   };

//   return (
//     <>
//       {modalShow && (
//         <ModalUser
//           userAuth="create"
//           setModalShow={setModalShow}
//           formId="create_user"
//         />
//       )}
//       <PageContent
//         depth01Title="관리자 설정"
//         depth02Title="사용자 관리"
//         depth03Title="계정 관리"
//       >
//         <>
//           <SearchContainer
//             onSearch={handleSearch}
//             components={[
//               <InputSelect
//                 name="searchType"
//                 size="md"
//                 placeholder="구분"
//                 options={[
//                   { value: "mbrUserId", label: "사용자ID" },
//                   { value: "mbrUserDepartment", label: "소속" },
//                   { value: "mbrUserRank", label: "계급" },
//                   { value: "mbrUserName", label: "성명" },
//                   { value: "mbrUserTell", label: "연락처" },
//                   { value: "mbrFirstTime", label: "등록일시" },
//                   { value: "mbrUserGroup", label: "권한" },
//                 ]}
//                 onChange={handleSearchTypeChange}
//               />,
//               <InputText
//                 type={searchInputType}
//                 name="searchValue"
//                 size="md"
//                 placeholder="Search"
//               />,
//             ]}
//             gridButtons={
//               <Buttons
//                 type="button"
//                 size="md"
//                 layout="primary"
//                 label="신규 등록"
//                 onClick={() => {
//                   setModalShow(!modalShow);
//                 }}
//               />
//             }
//           />
//           <Grid rowData={members} columnDefs={columnDefs} />
//         </>
//       </PageContent>
//     </>
//   );
// };
